import 'css/src/components/public-pages/contentful-components/audio-sync/AudioTrackItem';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { AudioTrack } from './useMediaBunnyMixer';

import TrimmerAudioTimeline from 'js/src/components/common/asset-trimmer/TrimmerAudioTimeline';
import { noop } from 'js/src/libs/consts';

interface AudioTrackItemProps {
    track: AudioTrack;
    totalDurationMs: number;
    timelineWidth: number;
    timelineStartMs: number;
    onOffsetChange: (id: string, offsetMs: number) => void;
    onRemove: (id: string) => void;
}

const TRACK_COLORS = [
    '#3b82f6',
    '#10b981',
    '#f59e0b',
    '#ef4444',
    '#8b5cf6',
    '#ec4899',
    '#06b6d4',
    '#84cc16',
];

function getTrackColor(index: number): string {
    return TRACK_COLORS[index % TRACK_COLORS.length];
}

// Simple hash function to get consistent color per track id
function hashCode(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash;
    }
    return Math.abs(hash);
}

const AudioTrackItem: React.FC<AudioTrackItemProps> = ({
    track,
    totalDurationMs,
    timelineWidth,
    timelineStartMs,
    onOffsetChange,
    onRemove,
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [dragStartX, setDragStartX] = useState(0);
    const [dragStartOffset, setDragStartOffset] = useState(0);
    const trackRef = useRef<HTMLDivElement>(null);

    const color = useMemo(() => getTrackColor(hashCode(track.id)), [track.id]);

    const audioSourceUrl = useMemo(() => URL.createObjectURL(track.file), [track.file]);

    useEffect(() => {
        return () => {
            URL.revokeObjectURL(audioSourceUrl);
        };
    }, [audioSourceUrl]);

    // Calculate track width and position based on duration and offset
    const msPerPixel = totalDurationMs / timelineWidth;
    const trackWidthPx = track.durationMs / msPerPixel;
    const trackLeftPx = (track.offsetMs - timelineStartMs) / msPerPixel;

    const handleMouseDown = useCallback(
        (e: React.MouseEvent) => {
            // Only start drag on left mouse button
            if (e.button !== 0) return;

            e.preventDefault();
            e.stopPropagation();
            setIsDragging(true);
            setDragStartX(e.clientX);
            setDragStartOffset(track.offsetMs);
        },
        [track.offsetMs],
    );

    useEffect(() => {
        if (!isDragging) return;

        const handleMouseMove = (e: MouseEvent) => {
            const deltaX = e.clientX - dragStartX;
            const deltaMs = deltaX * msPerPixel;
            const newOffset = dragStartOffset + deltaMs;
            onOffsetChange(track.id, newOffset);
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, dragStartX, dragStartOffset, msPerPixel, track.id, onOffsetChange]);

    const handleRemove = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            onRemove(track.id);
        },
        [track.id, onRemove],
    );

    return (
        <div
            ref={trackRef}
            className={`audio-track-item position-absolute d-flex flex-column overflow-hidden ${isDragging ? 'dragging' : ''}`}
            style={{
                left: `${trackLeftPx}px`,
                width: `${trackWidthPx}px`,
                backgroundColor: color,
                cursor: isDragging ? 'grabbing' : 'grab',
            }}
            onMouseDown={handleMouseDown}
        >
            <div className="audio-track-header d-flex justify-content-between align-items-center">
                <span className="audio-track-name">{track.name}</span>
                <button
                    onClick={handleRemove}
                    type="button"
                    className="audio-track-remove-btn d-flex align-items-center justify-content-center"
                >
                    <i className="material-symbols-outlined">close</i>
                </button>
            </div>

            <div className="audio-track-waveform">
                <TrimmerAudioTimeline
                    source={audioSourceUrl}
                    onLoaded={noop}
                    onAudioDetected={noop}
                    className="audio-track-waveform-inner w-100 h-100"
                />
            </div>
        </div>
    );
};

export default React.memo(AudioTrackItem);
